import { HttpService } from '@/shared/api/services/HttpService';
import { buildUrlForServiceName } from '@/connection/url-retriever';
import conf from '@/shared/config/useConfig';
import type { BürgeAnzeigedaten as BuergeAnzeigedaten, Land, Unternehmen } from '@/types/Partner';
import type { Buerge } from '@/shared/Buergen/types';
import type {
  UnternehmenFilter,
  UnternehmenFilterResponse,
  UnternehmenSucheFilter,
  UnternehmenSucheResponse,
  UnternehmenSucheRowItem,
} from '@/connection/filter-helper/filter-helper-unternehmen';
import type { TabelleServerParams } from '@/components/datatable/types/TabellenServerParams';
import type { PreisModell } from '@/shared/useFeatureToggles/preisModell';
import { configForJsonPostDownload } from '@/connection/base.ts';
import type { FilterOptionen } from '@/components/datatable/types/FilterObject.ts';

export class SharedPartnerService extends HttpService {
  protected partnerServiceBaseUrl = `${buildUrlForServiceName(
    conf.partnerserviceName,
  )}/partnerservice/api/private/v2`;

  async findeAlleUnternehmenNamenNachId(unternehmenIds: string[]): Promise<Unternehmen[]> {
    return this.httpClient.post(`${this.partnerServiceBaseUrl}/unternehmen/name`, {
      data: unternehmenIds,
    });
  }

  async findeUnternehmenNachId(id: string, cache: boolean = false): Promise<Unternehmen> {
    return this.httpClient.get(`${this.partnerServiceBaseUrl}/unternehmen/${id}`, {
      params: {
        cache,
      },
    });
  }

  async bearbeiteUnternehmenPreisModell(
    id: string,
    preisModell: keyof typeof PreisModell,
  ): Promise<void> {
    return this.httpClient.post(`${this.partnerServiceBaseUrl}/unternehmen/${id}/preismodell`, {
      data: {
        gueltigAb: new Date(Date.now()),
        preisModell,
      },
    });
  }

  async findeAlleBuergen(): Promise<Buerge[]> {
    return this.httpClient.get(`${this.partnerServiceBaseUrl}/buergen`);
  }

  async holeUnternehmenMitPaginierungUndFilterungUndSichtberechtigung(
    unternehmenFilter: UnternehmenFilter,
    serverParams: TabelleServerParams,
    filtereInaktive: boolean = false,
    filtereInkognito: boolean = false,
    filtereSichtberechtigung: boolean = false,
    auchFremdeUnternehmen: boolean = false,
    filtereUngepruefte: boolean = false,
  ): Promise<UnternehmenFilterResponse> {
    return this.httpClient.post(`${this.partnerServiceBaseUrl}/unternehmen/filterung`, {
      data: unternehmenFilter,
      params: {
        page: serverParams.page,
        size: serverParams.perPage,
        sort: `${serverParams.sort.field},${serverParams.sort.type}`,
        filtereInaktive,
        filtereInkognito,
        filtereSichtberechtigung,
        auchFremdeUnternehmen,
        filtereUngepruefte,
      },
    });
  }

  async holeTeilnehmerMitPaginierungUndFilterung(
    serverParams: TabelleServerParams,
    unternehmenFilter: UnternehmenFilter,
    filtereInaktive: boolean = false,
    filtereInkognito: boolean = false,
  ): Promise<UnternehmenFilterResponse> {
    return this.httpClient.post(`${this.partnerServiceBaseUrl}/unternehmen/teilnehmer/filterung`, {
      data: unternehmenFilter,
      params: {
        page: serverParams.page,
        size: serverParams.perPage,
        sort: `${serverParams.sort.field},${serverParams.sort.type}`,
        filtereInaktive,
        filtereInkognito,
      },
    });
  }

  async holeUnternehmenSucheMitPaginierungUndFilterung(
    seite: number,
    seitengroesse: number,
    property: string,
    sortierArt: string,
    unternehmenSucheFilter: UnternehmenSucheFilter,
  ): Promise<UnternehmenSucheResponse> {
    return this.httpClient.post(`${this.partnerServiceBaseUrl}/unternehmen/suche`, {
      data: unternehmenSucheFilter,
      params: {
        page: seite,
        size: seitengroesse,
        sort: `${property},${sortierArt}`,
      },
    });
  }

  async holeUnternehmenSucheOhnePaginierungUndMitFilterung(
    seite: number,
    seitengroesse: number,
    property: string,
    sortierArt: string,
    unternehmenSucheFilter: UnternehmenSucheFilter,
    filtereInaktive?: boolean,
    filtereInkognito?: boolean,
    filtereSichtberechtigung?: boolean,
    auchFremdeUnternehmen?: boolean,
    filtereUngepruefte?: boolean,
  ): Promise<UnternehmenSucheRowItem[]> {
    return this.httpClient.post(`${this.partnerServiceBaseUrl}/unternehmen/suche/ohnepaging`, {
      data: unternehmenSucheFilter,
      params: {
        page: seite,
        size: seitengroesse,
        sort: `${property},${sortierArt}`,
        filtereInaktive,
        filtereInkognito,
        filtereSichtberechtigung,
        auchFremdeUnternehmen,
        filtereUngepruefte,
      },
    });
  }

  async holeBuergenMitPaginierungUndFilterung(
    serverParams: TabelleServerParams,
    unternehmenFilter: UnternehmenFilter,
    filtereInaktive?: boolean,
    filtereInkognito?: boolean,
    filtereSichtberechtigung?: boolean,
    auchFremdeUnternehmen?: boolean,
    filtereNutzungVertraegeAktiviert?: boolean,
  ): Promise<UnternehmenFilterResponse> {
    return this.httpClient.post(`${this.partnerServiceBaseUrl}/unternehmen/buergen/filterung`, {
      data: unternehmenFilter,
      params: {
        page: serverParams.page,
        size: serverParams.perPage,
        sort: `${serverParams.sort.field},${serverParams.sort.type}`,
        filtereInaktive,
        filtereInkognito,
        filtereSichtberechtigung,
        auchFremdeUnternehmen,
        filtereNutzungVertraegeAktiviert,
      },
    });
  }

  async downloadAlleUnternehmenCsv(unternehmenFilter: UnternehmenFilter): Promise<BlobPart> {
    return this.httpClient.post(
      `${this.partnerServiceBaseUrl}/unternehmen/filterung/unternehmen.csv`,
      { data: unternehmenFilter, config: configForJsonPostDownload },
    );
  }

  async findeLaenderliste(): Promise<Land[]> {
    return this.httpClient.get(`${this.partnerServiceBaseUrl}/laenderliste`);
  }

  async holeBuergeAnzeigedatenNachId(
    id: string,
    cache: boolean = false,
  ): Promise<BuergeAnzeigedaten> {
    return this.httpClient.get(`${this.partnerServiceBaseUrl}/buergen/${id}/anzeigedaten`, {
      params: { cache },
    });
  }

  async istBuergeMitFremdKurznameBereitsVorhanden(kurzname: string): Promise<boolean> {
    return this.httpClient.get(`${this.partnerServiceBaseUrl}/buergen/istVorhanden`, {
      params: { kurzname },
    });
  }

  async holeFilterOptionenUnternehmen(filtereInaktive?: boolean): Promise<FilterOptionen> {
    return this.httpClient.get(`${this.partnerServiceBaseUrl}/unternehmen/filteroptionen`, {
      params: { filtereInaktive: filtereInaktive || false },
    });
  }
}
